/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, atom } from 'jotai';
import { atomFamily, unwrap, useAtomCallback } from 'jotai/utils';
import { availableWorkspacesSelectorFamily, WorkspaceObject } from 'jotai/workspace';
import { getDefaultSystemWorkspaceId } from 'jotai/workspace/WorkspaceDefaultFactory';
import { AdminSettingsId, OriginType, RunType } from 'models/viewModels';
import { useCallback } from 'react';
import { adminSettingsService } from 'services/adminSettings/AdminSettingsService';
import { systemWorkspaceService } from 'services/workspace/SystemWorkspaceService';
import {
  AudioTranscriptionAdminSettingsObject,
  DefaultWorkspaceSettingsObject,
  RaceProgressionOriginAdminSettingsObject,
} from './AdminSettingsObjects';

export const adminSettingsBySettingsIdAtomFamily = atomFamily<AdminSettingsId, Atom<Promise<string>>>(
  (id: AdminSettingsId) =>
    atom<Promise<string>>(async (_get) => {
      return await adminSettingsService.getAdminSettingsBySettingsId(id);
    })
);

const overwrittenAudioTranscriptionSettings = atom<AudioTranscriptionAdminSettingsObject | undefined>(undefined);
const adminSettingsAudioTranscriptionSettings = atom<Promise<AudioTranscriptionAdminSettingsObject | undefined>>(
  async (get) => {
    if (get(overwrittenAudioTranscriptionSettings) !== undefined) {
      return get(overwrittenAudioTranscriptionSettings);
    } else {
      return await adminSettingsService.getAudioTranscriptionAdminSettings();
    }
  }
);

const overwrittenAudioFilteringEnabledAtom = atom<boolean | undefined>(undefined);
export const adminSettingsAudioFilteringEnabledAtom = unwrap(
  atom<Promise<boolean>, [boolean], void>(
    async (get) => {
      const overwritten = get(overwrittenAudioFilteringEnabledAtom);
      if (overwritten !== undefined) {
        return overwritten;
      } else {
        return (await adminSettingsService.getAudioTranscriptionAdminSettings()).speakerFilterEnabled || false;
      }
    },
    (_get, set, newValue: boolean) => {
      set(overwrittenAudioFilteringEnabledAtom, newValue);
    }
  )
);

const overwrittenAudioThresholdPoorAtom = atom<number | undefined>(undefined);
export const adminSettingsAudioTranscriptionThresholdPoorAtom = unwrap(
  atom<Promise<number | undefined>, [number], void>(
    async (get) => {
      if (get(overwrittenAudioThresholdPoorAtom) !== undefined) {
        return get(overwrittenAudioThresholdPoorAtom);
      } else {
        const value = await adminSettingsService.getAudioTranscriptionAdminSettings();
        if (value.thresholdPoor != null && value.thresholdPoor >= 0) {
          return value.thresholdPoor;
        } else {
          return 300;
        }
      }
    },
    (_get, set, newValue: number) => {
      set(overwrittenAudioThresholdPoorAtom, newValue);
    }
  )
);

const overwrittenAudioThresholdModerateAtom = atom<number | undefined>(undefined);
export const adminSettingsAudioTranscriptionThresholdModerateAtom = unwrap(
  atom<Promise<number | undefined>, [number], void>(
    async (get) => {
      if (get(overwrittenAudioThresholdModerateAtom) !== undefined) {
        return get(overwrittenAudioThresholdModerateAtom);
      } else {
        const value = await adminSettingsService.getAudioTranscriptionAdminSettings();
        if (value.thresholdModerate != null && value.thresholdModerate >= 0) {
          return value.thresholdModerate;
        } else {
          return 120;
        }
      }
    },
    (_get, set, newValue: number) => {
      set(overwrittenAudioThresholdModerateAtom, newValue);
    }
  )
);

const overwrittenAdminSettingsDefaultWorkspace = atom<DefaultWorkspaceSettingsObject>(
  {} as DefaultWorkspaceSettingsObject
);
export const adminSettingsDefaultWorkspaceAtom = atom<
  Promise<DefaultWorkspaceSettingsObject>,
  [DefaultWorkspaceSettingsObject],
  void
>(
  async (get) => {
    if (Object.keys(get(overwrittenAdminSettingsDefaultWorkspace)).length > 0) {
      return get(overwrittenAdminSettingsDefaultWorkspace);
    }
    return await adminSettingsService.getDefaultWorkspaceAdminSettings();
  },
  (_get, set, newValue: DefaultWorkspaceSettingsObject) => {
    set(overwrittenAdminSettingsDefaultWorkspace, newValue);
  }
);

export const adminSettingsDefaultWorkspaceSelectorFamily = atomFamily<RunType, Atom<Promise<WorkspaceObject>>>(
  (sessionType: RunType) =>
    atom<Promise<WorkspaceObject>>(async (get) => {
      const adminDefaultWorkspace = await get(adminSettingsDefaultWorkspaceAtom);
      const workspaces = await get(availableWorkspacesSelectorFamily(sessionType));

      let defaultAdminWorkspaceId = '';
      if (adminDefaultWorkspace) {
        switch (sessionType) {
          case RunType.PRACTICE:
            defaultAdminWorkspaceId = adminDefaultWorkspace.PracticeDefaultWorkspaceId;
            break;
          case RunType.QUALIFYING:
            defaultAdminWorkspaceId = adminDefaultWorkspace.QualifyingDefaultWorkspaceId;
            break;
          case RunType.RACE:
            defaultAdminWorkspaceId = adminDefaultWorkspace.RaceDefaultWorkspaceId;
            break;
        }
      }
      if (!defaultAdminWorkspaceId) {
        defaultAdminWorkspaceId = getDefaultSystemWorkspaceId(sessionType);
      }
      return (
        workspaces.find((workspace) => workspace.id === defaultAdminWorkspaceId) ||
        systemWorkspaceService.getWorkspace(getDefaultSystemWorkspaceId(sessionType))
      );
    })
);

export const useUpsertAdminAudioSettings = () => {
  return useAtomCallback(
    useCallback(async (get, set, settings: AudioTranscriptionAdminSettingsObject) => {
      const prevSettings = await get(adminSettingsAudioTranscriptionSettings);
      const newSettings = {
        ...prevSettings,
        ...settings,
      };
      const response = await adminSettingsService.updateAdminSettings(
        AdminSettingsId.AUDIO_TRANSCRIPTION,
        JSON.stringify(newSettings)
      );
      if (response.status) {
        set(overwrittenAudioTranscriptionSettings, newSettings);
      }
    }, [])
  );
};

const overwrittenAdminRaceProgressionOriginsAtom = atom<RaceProgressionOriginAdminSettingsObject | undefined>(
  undefined
);
const adminSettingsRaceProgressionOriginsAtom = atom<Promise<RaceProgressionOriginAdminSettingsObject | undefined>>(
  async (get) => {
    if (get(overwrittenAdminRaceProgressionOriginsAtom) !== undefined) {
      return get(overwrittenAdminRaceProgressionOriginsAtom);
    } else {
      return await adminSettingsService.getRaceProgressionOriginsAdminSettings();
    }
  }
);

const overwrittenAdminNascarRaceProgressionOriginAtom = atom<OriginType | undefined>(undefined);
export const adminSettingsNascarRaceProgressionOriginAtom = unwrap(
  atom<Promise<OriginType | undefined>, [OriginType], void>(
    async (get) => {
      if (get(overwrittenAdminNascarRaceProgressionOriginAtom) !== undefined) {
        return get(overwrittenAdminNascarRaceProgressionOriginAtom);
      } else {
        const value = await adminSettingsService.getRaceProgressionOriginsAdminSettings();
        if (value.nascarOrigin != null) {
          return value.nascarOrigin;
        } else {
          return OriginType.ERDP;
        }
      }
    },
    (_get, set, newValue: OriginType) => {
      set(overwrittenAdminNascarRaceProgressionOriginAtom, newValue);
    }
  )
);

const overwrittenAdminIndyRaceProgressionOriginAtom = atom<OriginType | undefined>(undefined);
export const adminSettingsIndyRaceProgressionOriginAtom = unwrap(
  atom<Promise<OriginType | undefined>, [OriginType], void>(
    async (get) => {
      if (get(overwrittenAdminIndyRaceProgressionOriginAtom) !== undefined) {
        return get(overwrittenAdminIndyRaceProgressionOriginAtom);
      } else {
        const value = await adminSettingsService.getRaceProgressionOriginsAdminSettings();
        if (value.indycarOrigin != null) {
          return value.indycarOrigin;
        } else {
          return OriginType.MULTILOOP;
        }
      }
    },
    (_get, set, newValue: OriginType) => {
      set(overwrittenAdminIndyRaceProgressionOriginAtom, newValue);
    }
  )
);

export const useUpsertAdminRaceProgressionOrigins = () => {
  return useAtomCallback(
    useCallback(async (get, set, settings: RaceProgressionOriginAdminSettingsObject) => {
      const prevSettings = await get(adminSettingsRaceProgressionOriginsAtom);
      const newSettings = {
        ...prevSettings,
        ...settings,
      };
      const response = await adminSettingsService.updateAdminSettings(
        AdminSettingsId.RACE_PROGRESSION_ORIGINS,
        JSON.stringify(newSettings)
      );
      if (response.status) {
        set(overwrittenAdminRaceProgressionOriginsAtom, newSettings);
      }
    }, [])
  );
};

export const useUpsertAdminSettings = () => {
  return useAtomCallback(
    useCallback(async (_get, _set, settingsId: AdminSettingsId, settings: string) => {
      await adminSettingsService.updateAdminSettings(settingsId, settings);
    }, [])
  );
};
